import { Alert } from "PFComponents/alert";
import { profileHash } from "PFCore/helpers/profile_hash";
import PropTypes from "prop-types";
import React from "react";
import { prune } from "underscore.string";

import PagePreview from "./page_preview";
import css from "./pptx_preview.less";

const Positions = ({ positions, titleColor }) => {
  if (!positions?.items || positions.items.length === 0) {
    return false;
  }

  return (
    <>
      <strong className={css.subheader} style={{ color: titleColor }}>
        Experience
      </strong>
      <ul className={css.positions}>
        {positions.items.slice(0, 3).map(({ id, jobTitle, companyName, summary }) => (
          <li key={id}>
            <strong className={css.text}>
              {jobTitle}
              {companyName ? ` in ${companyName}` : false}
            </strong>
            <div className={css.text}>{prune(summary, 300)}</div>
          </li>
        ))}
      </ul>
    </>
  );
};

const Contacts = ({ contactInformationItems, profile }) => (
  <>
    {[{ type: "Email", text: profile.email }, ...contactInformationItems]
      .slice(0, 3)
      .map((contact, index) => (
        <div className={css.textSidebar} key={`contract_information_${index}`}>
          <strong>{contact.type}:</strong>
          <span>{` ${contact.text}`}</span>
        </div>
      ))}
  </>
);

const Summary = ({ summary, titleColor }) => {
  if (!summary) {
    return false;
  }

  return (
    <>
      <strong className={css.subheader} style={{ color: titleColor }}>
        Summary
      </strong>
      <p className={css.text}>{prune(summary, 500)}</p>
    </>
  );
};

const CustomField = ({ customTypes, typeName, titleColor, bullet }) => {
  const customField = customTypes.find(({ type }) => type.name === typeName);

  if (!customField || !customField.text) {
    return false;
  }

  return (
    <div className={css.customType}>
      <strong className={css.subheaderSidebar} style={{ color: titleColor }}>
        {customField.displayAs}
      </strong>
      {bullet ? (
        <ul>
          {customField.text
            .split(", ")
            .slice(0, 3)
            .map((item) => (
              <li key={item}>
                <div className={css.textSidebar}>{item}</div>
              </li>
            ))}
        </ul>
      ) : (
        <p className={css.text}>{prune(customField.text, 150)}</p>
      )}
    </div>
  );
};

const PptxPreview = ({ profile, order, config, avatar, logo }) => {
  const { color, color2 } = config;

  return (
    <>
      {profile.enabled === false && (
        <Alert kind="warning" title="Disabled" style={{ marginBottom: 20, marginTop: 20 }}>
          This profile won&apos;vt be exported when exporting all profiles at once because it&apos;vs disabled
          in the <b>Profiles</b> tab
        </Alert>
      )}
      <PagePreview
        style={{
          height: 450,
          minHeight: 450,
          padding: 20,
          display: "flex"
        }}
      >
        <div className={css.sidebar} style={{ borderColor: color2 }}>
          {avatar && !config.isBlindModeOn && <img alt="avatar" className={css.avatar} src={avatar} />}

          {!config.isBlindModeOn && (
            <Contacts contactInformationItems={profile.contactInformationItems} profile={profile} />
          )}
          {order
            .filter((name) => !config.hidden[name] && ["certificates", "qualifications"].includes(name))
            .map((name) => (
              <CustomField
                customTypes={profile.customTypes}
                key={name}
                typeName={name}
                titleColor={color}
                bullet
              />
            ))}
          <img className={css.logo} src={logo.url} height={20} alt="" />
        </div>
        <div className={css.main}>
          <strong className={css.profileName} style={{ color }}>
            {config.isBlindModeOn ? `Candidate ${profileHash(profile.id)}` : profile.name}
          </strong>
          <p className={css.jobTitle} style={{ color: color2 }}>
            {profile.jobTitle}
          </p>
          {order.map((name) => {
            if (config.hidden[name]) {
              return false;
            } else if (name === "summary") {
              return <Summary summary={profile.summary} key={name} titleColor={color} />;
            } else if (name === "experience") {
              return <Positions positions={profile.positions} key={name} titleColor={color} />;
            }
          })}
        </div>
      </PagePreview>
    </>
  );
};

const positionItemShape = PropTypes.shape({
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      jobTitle: PropTypes.string,
      companyName: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      current: PropTypes.bool,
      summary: PropTypes.string
    })
  )
});

Summary.propTypes = {
  summary: PropTypes.string,
  titleColor: PropTypes.string
};

CustomField.propTypes = {
  customTypes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string
    })
  ),
  typeName: PropTypes.string,
  bullet: PropTypes.bool,
  titleColor: PropTypes.string
};

Contacts.propTypes = {
  contactInformationItems: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      text: PropTypes.string
    })
  ),
  profile: PropTypes.shape({
    email: PropTypes.string
  })
};

Positions.propTypes = {
  positions: positionItemShape,
  titleColor: PropTypes.string
};

PptxPreview.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number,
    enabled: PropTypes.bool,
    name: PropTypes.string,
    jobTitle: PropTypes.string,
    companyName: PropTypes.string,
    summary: PropTypes.string,
    contactInformationItems: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        text: PropTypes.string
      })
    ),
    positions: positionItemShape,
    customTypes: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string }))
  }),
  config: PropTypes.shape({
    color: PropTypes.string,
    color2: PropTypes.string,
    hidden: PropTypes.object,
    logo: PropTypes.string,
    isBlindModeOn: PropTypes.bool
  }),
  order: PropTypes.arrayOf(PropTypes.string),
  avatar: PropTypes.string,
  logo: PropTypes.shape({
    url: PropTypes.string,
    custom: PropTypes.bool
  })
};

export default PptxPreview;
